/**
 * Endpoints de **Finanzas → Izipay** (`/api/finanzas/izipay`).
 *
 * El listado llega paginado con sus totales, el desglose por método y por día y
 * los catálogos de los filtros (POS, métodos, marcas y estados). La anulación es
 * cosa de administradores y el comprobante sale como ticket en PDF.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import type { DetalleIzipay, FiltrosIzipay, ListadoIzipay } from './izipay.types';

export const izipayKeys = {
  todo: ['finanzas', 'izipay'] as const,
  lista: (filtros: FiltrosIzipay) => ['finanzas', 'izipay', 'lista', filtros] as const,
  detalle: (id: number) => ['finanzas', 'izipay', 'detalle', id] as const,
};

/** Cadena de consulta del listado y de las exportaciones. */
export function cadenaDeIzipay(filtros: FiltrosIzipay): string {
  const parametros = new URLSearchParams();

  if (filtros.desde) parametros.set('desde', filtros.desde);
  if (filtros.hasta) parametros.set('hasta', filtros.hasta);
  if (filtros.metodo) parametros.set('metodo', filtros.metodo);
  if (filtros.pos) parametros.set('pos', filtros.pos);
  if (filtros.estado) parametros.set('estado', filtros.estado);
  if (filtros.anulado !== '') parametros.set('anulado', filtros.anulado);
  if (filtros.marca) parametros.set('marca', filtros.marca);
  if (filtros.buscar) parametros.set('buscar', filtros.buscar);
  if (filtros.page > 1) parametros.set('page', String(filtros.page));
  if (filtros.perPage) parametros.set('per_page', String(filtros.perPage));

  const cadena = parametros.toString();

  return cadena ? `?${cadena}` : '';
}

export const izipayApi = {
  listar: (filtros: FiltrosIzipay, signal?: AbortSignal) =>
    apiClient.get<ListadoIzipay>(`/api/finanzas/izipay${cadenaDeIzipay(filtros)}`, { signal }),

  detalle: (id: number, signal?: AbortSignal) =>
    apiClient.get<DetalleIzipay>(`/api/finanzas/izipay/${id}`, { signal }),

  anular: (id: number, dispositivo: string) =>
    apiClient.post<{ id: number; message: string }>(`/api/finanzas/izipay/${id}/anular`, {
      body: { dispositivo },
    }),

  urlComprobante: (id: number) => `/api/finanzas/izipay/${id}/comprobante`,

  urlExportar: (filtros: FiltrosIzipay, formato: 'csv' | 'pdf') => {
    const cadena = cadenaDeIzipay(filtros);

    return formato === 'csv'
      ? `/api/finanzas/izipay/exportar${cadena}`
      : `/api/finanzas/izipay/pdf${cadena}`;
  },
};

/** Listado paginado con sus totales, desglose y catálogos. */
export function useIzipay(filtros: FiltrosIzipay) {
  return useQuery({
    queryKey: izipayKeys.lista(filtros),
    queryFn: ({ signal }) => izipayApi.listar(filtros, signal),
    placeholderData: (anterior) => anterior,
  });
}

/** Ficha completa de una transacción. */
export function useTransaccionIzipay(id: number | null) {
  return useQuery({
    queryKey: izipayKeys.detalle(id ?? 0),
    queryFn: ({ signal }) => izipayApi.detalle(id as number, signal),
    enabled: Number(id) > 0,
  });
}

/** Anulación de la transacción (sólo administradores). */
export function useAnularIzipay() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: ({ id, dispositivo }: { id: number; dispositivo: string }) =>
      izipayApi.anular(id, dispositivo),
    onSuccess: () => cliente.invalidateQueries({ queryKey: izipayKeys.todo }),
  });
}
